import { ConfigProvider } from 'shared/config/ConfigContext';
import hotelData from 'shared/seo/data/hotel.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { DestinationPage } from '@/components/DestinationPage';
import { countryLevel1Factory, hotelSearchFactory } from '@/components/factory';
import { feConfig } from '@/config/index.mjs';
import { getCountriesForDestination, hotelCountries, hotelMapping } from '@/data';
import { verticals } from '@/data/verticals';
import type { CountryPageProps, SeoPageContext } from '@/interfaces/pages';

export async function getStaticPaths() {
    const destinations = hotelCountries.map((place) => ({
        params: { countryLevel1: place.countryPath },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
}

export async function getStaticProps(context: SeoPageContext) {
    const destination = hotelCountries.find((place) => place.countryPath === context.params.countryLevel1.toLowerCase());

    const popularCities = (hotelMapping[destination.id] ?? []).slice(0, 11);
    const popularCountries = getCountriesForDestination(destination, hotelCountries);

    return {
        props: {
            destination,
            popularCities,
            popularCountries,
            config: feConfig,
        },
    };
}

const CountryLevel1 = ({ destination, popularCities, popularCountries, config }: CountryPageProps) => {
    const factory = countryLevel1Factory(destination, hotelSearchFactory());

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.hotel} data={hotelData}>
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={hotelData.cdnPath}
                    verticalUrl={hotelData.basePath}
                    lang={hotelData.lang}
                />
                <PopularCountries
                    destination={destination}
                    countries={popularCountries}
                    cdnUrl={hotelData.cdnPath}
                    verticalUrl={hotelData.basePath}
                    lang={hotelData.lang}
                />
                <LinkTree
                    heading={hotelData.lang.linkTree.heading}
                    description={hotelData.lang.linkTree.description}
                    data={hotelData.linkTree}
                    url={hotelData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CountryLevel1;
